/**
 * Textos de `english` en español: la evaluación de inglés por llamada, en sus
 * tres caras (agencia, candidato, operación) y el informe que comparten.
 *
 * Voz de TBit: tutea, palabras simples, sin raya larga, nunca «automatizar».
 */
const english = {
  /** El estado, igual para todos. */
  status: {
    requested: 'Pedida',
    notified: 'Avisada',
    in_call: 'En llamada',
    scoring: 'Calificando',
    completed: 'Lista',
    expired: 'Vencida',
    canceled: 'Cancelada',
    failed: 'Con error',
  },

  /** El informe (agencia y candidato ven el mismo). */
  report: {
    level: 'Nivel de inglés',
    insufficient: 'Sin evidencia suficiente',
    insufficientNote:
      'La conversación en inglés fue muy corta para dar un nivel. Hacen falta al menos tres minutos hablando en inglés.',
    confidence: { low: 'Confianza baja', medium: 'Confianza media', high: 'Confianza alta' },
    dimensions: {
      fluency: 'Fluidez',
      grammar: 'Gramática',
      vocabulary: 'Vocabulario',
      comprehension: 'Comprensión',
      pronunciation: 'Pronunciación',
    },
    pronunciationPending: 'Todavía no la medimos',
    pronunciationNotMeasured: 'No la pudimos medir en esta llamada',
    pronunciationIssues: 'Para practicar',
    issueAt: (at: string) => `minuto ${at}`,
    recording: 'Grabación de la llamada',
    listen: 'Escuchar la llamada',
    recordingLoading: 'Abriendo la grabación',
    recordingFailed: 'No pudimos abrir la grabación. Intenta de nuevo.',
    recordingPrivacy:
      'Es privada: solo la escuchan la empresa que pidió la evaluación y el candidato.',
    recordingUntil: (date: string) => `Disponible hasta el ${date}.`,
    evidence: 'Lo que dijo',
    transcript: 'Ver la transcripción',
    hideTranscript: 'Ocultar la transcripción',
    interviewer: 'Entrevistadora',
    candidate: 'Candidato',
    scoredOn: (date: string) => `Calificada el ${date}`,
    reused: 'Resultado de una evaluación reciente de esta persona: no tuvo que repetir la llamada.',
    disclaimer:
      'Es una preselección hecha por una IA a partir de la conversación. Úsala junto con tu propia entrevista.',
  },

  /** La consola de agencia (tarjeta del candidato). */
  agency: {
    title: 'Inglés',
    request: 'Evaluar inglés',
    confirmTitle: '¿Evaluamos su inglés?',
    confirmBody:
      'Le pedimos al candidato que llame por WhatsApp a TBit Jobs. Lo atiende una entrevistadora de inteligencia artificial que conversa con él en inglés unos 10 minutos. La llamada se graba y el resultado lo ven tú y el candidato.',
    confirmDeadline: 'Tiene 7 días para llamar. Tú no ves su número.',
    confirm: 'Sí, pedir la evaluación',
    cancel: 'Ahora no',
    cancelRequest: 'Cancelar la evaluación',
    loading: 'Cargando la evaluación',
    requestFailed: 'No pudimos pedir la evaluación. Intenta de nuevo.',
    cancelFailed: 'No pudimos cancelarla.',
    waiting: (date: string) => `Le avisamos al candidato. Puede llamar hasta el ${date}.`,
    inCall: 'El candidato está en la llamada ahora.',
    scoring: 'La llamada terminó. Estamos calificando, tarda un par de minutos.',
    retryChip: 'Esperando otro intento',
    shortCallChip: 'Llamada corta, esperando otro intento',
    shortCall: (left: number) =>
      left === 1
        ? 'La última llamada fue muy corta para calificar. Le queda 1 intento.'
        : `La última llamada fue muy corta para calificar. Le quedan ${left} intentos.`,
    expired: 'Venció sin una llamada completa. Puedes pedirla de nuevo.',
    canceled: 'La cancelaste. Puedes pedirla de nuevo.',
    failed: 'No pudimos calificar la llamada. Ya lo estamos revisando.',
    askAgain: 'Pedir de nuevo',
    refresh: 'Actualizar',
  },

  /** El candidato: la tarjeta para llamar y la página de Perfil. */
  candidate: {
    cardTitle: (company: string) => `${company} quiere conocer tu nivel de inglés`,
    cardTitleFallback: 'Una empresa quiere conocer tu nivel de inglés',
    forPosting: (title: string) => `Para la vacante ${title}.`,
    howItWorks:
      'Es una llamada corta por WhatsApp con una entrevistadora de inteligencia artificial de TBit. Dura unos 10 minutos, la conversación es en inglés y se graba para calificarla. El resultado lo ven la empresa y tú.',
    free: 'La llamada es gratis.',
    deadline: (date: string) => `Tienes hasta el ${date}.`,
    tips: 'Busca un lugar tranquilo. Responde con calma y con frases completas: no hay respuestas malas.',
    phoneLabel: 'Tu número de WhatsApp',
    phoneHelp: 'Llama desde este número: así sabemos que eres tú.',
    phoneMissing: 'Antes de llamar, dinos tu número de WhatsApp.',
    phoneCountry: 'País',
    phoneNumber: 'Número',
    phonePlaceholder: '300 123 4567',
    phoneSave: 'Guardar número',
    phoneEdit: 'Cambiar',
    phoneConfirmQuestion: '¿Es este tu WhatsApp? Confírmalo antes de llamar.',
    phoneConfirm: 'Sí, es este',
    phoneSaved: 'Listo, guardamos tu número.',
    phoneInvalid: 'Revisa el número: escríbelo sin el indicativo del país.',
    phoneFailed: 'No pudimos guardar tu número. Intenta de nuevo.',
    call: 'Llamar por WhatsApp',
    callFallback: (number: string) =>
      `Si el botón no abre la llamada (por ejemplo, desde un computador), abre WhatsApp en tu celular y llama al ${number}.`,
    callFirst: 'Primero guarda tu número de WhatsApp.',
    inCall: 'Estás en la llamada. Cuando cuelgues, calificamos tu inglés.',
    scoring: 'Gracias por llamar. Estamos calificando tu inglés, tarda un par de minutos.',
    shortCall: (left: number) =>
      left === 1
        ? 'Tu última llamada fue muy corta para darte un nivel. Te queda 1 intento: llama otra vez y habla un poco más.'
        : `Tu última llamada fue muy corta para darte un nivel. Te quedan ${left} intentos: llama otra vez y habla un poco más.`,
    resultTitle: 'Tu resultado de inglés',
    resultFor: (company: string, title: string) => `Para ${company} · ${title}`,
    sharedWith: (company: string) => `${company} también ve este resultado.`,
    seeAll: 'Ver mis evaluaciones de inglés',
    pageTitle: 'Tus evaluaciones de inglés',
    emptyTitle: 'Todavía no tienes evaluaciones de inglés',
    emptyNote: 'Cuando una empresa quiera conocer tu nivel de inglés, te avisamos aquí y por correo.',
    pastTitle: 'Evaluaciones anteriores',
    expiredNote: 'Venció sin una llamada completa.',
    canceledNote: 'La empresa la canceló.',
    failedNote: 'No pudimos calificar la llamada. Te escribimos apenas lo resolvamos.',
    loadError: 'No pudimos cargar tus evaluaciones de inglés. Recarga la página.',
  },

  /** La consola de operación. */
  admin: {
    title: 'Evaluaciones de inglés',
    byStatus: 'Por estado',
    recent: 'Últimas 20',
    cost: (usd: string) => `Costo acumulado: US$${usd}`,
    empty: 'Todavía no hay evaluaciones.',
    columns: {
      date: 'Fecha',
      agency: 'Agencia',
      posting: 'Vacante',
      candidate: 'Candidato',
      phone: 'WhatsApp',
      status: 'Estado',
      calls: 'Llamadas',
      level: 'Nivel',
    },
    reused: 'reutilizada',
    shared: 'compartida',
    demo: 'demo',
    rescore: 'Calificar de nuevo',
    rescoreFailed: 'No se pudo volver a calificar.',
  },
};

export default english;
